import { useCallback, useEffect, useState } from "react";

/**
 * Hook for resizable table columns.
 * @param {number[]} defaultWidths - Initial min widths for each column (px)
 * @returns {{ widths: number[], getColProps: (i: number) => object, ResizeHandle: React.ComponentType }}
 */
export function useResizableColumns(defaultWidths) {
  const [widths, setWidths] = useState(() => defaultWidths || []);
  const [resizing, setResizing] = useState({ active: false, index: null, startX: 0, startWidth: 0 });

  const handleMouseDown = useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing({
      active: true,
      index,
      startX: e.clientX,
      startWidth: widths[index] ?? 150
    });
  }, [widths]);

  useEffect(() => {
    if (!resizing.active || resizing.index == null) return;

    const handleMouseMove = (e) => {
      const delta = e.clientX - resizing.startX;
      const newWidth = Math.max(60, resizing.startWidth + delta);
      setWidths((prev) => {
        const next = [...prev];
        next[resizing.index] = newWidth;
        return next;
      });
    };

    const handleMouseUp = () => setResizing({ active: false, index: null, startX: 0, startWidth: 0 });

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [resizing.active, resizing.index]);

  const getColProps = useCallback((i) => {
    const w = widths[i] ?? defaultWidths?.[i] ?? 150;
    return { style: { width: w, minWidth: w, maxWidth: 600 } };
  }, [widths, defaultWidths]);

  const ResizeHandle = useCallback(({ index }) => (
    <div
      role="separator"
      aria-orientation="vertical"
      className="table-resize-handle"
      onMouseDown={(e) => handleMouseDown(e, index)}
    />
  ), [handleMouseDown]);

  return { widths, getColProps, ResizeHandle };
}
